import type {
  Recipient,
  DashboardStats,
  InstitutionStats,
  PublicStats,
  RecipientsResponse,
  AttendanceLogsResponse,
  UploadLog,
  CSVRow,
  UploadResponse,
  CsvImportPayload,
  RecipientField,
  UndoUploadResponse,
} from "../types";

// Base URL for all API calls.
// • Dev:  Vite proxies /api → http://localhost:3001 (see vite.config.ts)
// • Prod: same domain serves /api directly
// • Override at build time: VITE_API_BASE=https://api.example.com/api
const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

// ── helpers ────────────────────────────────────────────────────────────────

const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};

const parseJson = async (res: Response) => {
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) {
    const text = await res.text();
    throw new Error(`Non-JSON response: ${text}`);
  }
  return res.json();
};

// ── token helpers ──────────────────────────────────────────────────────────

const TOKEN_KEY = "admin_token";

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clearToken: () => localStorage.removeItem(TOKEN_KEY),

  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),

  /** Returns { user } or null — mirrors the old client.auth.getSession() shape */
  getSession: async (): Promise<{ data: { user: { email: string } | null } }> => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return { data: { user: null } };
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        localStorage.removeItem(TOKEN_KEY);
        return { data: { user: null } };
      }
      const data = await res.json();
      return { data: { user: data.user } };
    } catch {
      return { data: { user: null } };
    }
  },

  login: async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? "Log masuk gagal");
    auth.setToken(data.token);
    return data;
  },

  signOut: async () => {
    auth.clearToken();
  },
};

const authFetch = (path: string, init?: RequestInit) => {
  const token = auth.getToken();
  return fetch(path, {
    ...init,
    headers: {
      ...init?.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// ── public API ─────────────────────────────────────────────────────────────

export const publicApi = {
  searchRecipient: async (query: string) => {
    const res = await fetch(`${API_BASE}/public/recipient/search?q=${encodeURIComponent(query)}`);
    const data = await parseJson(res);
    if (data.recipient) data.recipient = toCamelCase(data.recipient);
    return data;
  },

  getStats: async (): Promise<PublicStats> => {
    const res = await fetch(`${API_BASE}/public/stats`);
    return toCamelCase(await parseJson(res));
  },
};

// ── protected API ──────────────────────────────────────────────────────────

export const api = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    const res = await authFetch(`${API_BASE}/dashboard/stats`);
    return toCamelCase(await parseJson(res));
  },

  getInstitutionStats: async (limit = 20): Promise<InstitutionStats> => {
    const res = await authFetch(`${API_BASE}/institutions/stats?limit=${limit}`);
    return toCamelCase(await parseJson(res));
  },

  getJurusanStats: async (limit = 20): Promise<any> => {
    const res = await authFetch(`${API_BASE}/jurusan/stats?limit=${limit}`);
    return toCamelCase(await parseJson(res));
  },

  getRecipients: async (params?: {
    page?: number; limit?: number; search?: string;
    institution?: string; dun?: string; status?: string; slotKod?: string; age?: string;
  }): Promise<RecipientsResponse> => {
    const q = new URLSearchParams();
    if (params?.page)    q.set("page",     String(params.page));
    if (params?.limit)   q.set("limit",    String(params.limit));
    if (params?.search)  q.set("search",   params.search);
    if (params?.institution) q.set("institution", params.institution);
    if (params?.dun)     q.set("dun",      params.dun);
    if (params?.slotKod) q.set("slotKod",  params.slotKod);
    if (params?.age)      q.set("age", params.age);
    if (params?.status !== undefined) q.set("status", params.status);
    const res = await authFetch(`${API_BASE}/recipients?${q}`);
    return toCamelCase(await parseJson(res));
  },

  getRecipient: async (id: number): Promise<{ recipient: Recipient }> => {
    const res = await authFetch(`${API_BASE}/recipients/${id}`);
    const data = await parseJson(res);
    if (data.recipient) data.recipient = toCamelCase(data.recipient);
    return data;
  },

  checkIn: async (id: number, checkedInBy?: string, options?: {
    type?: "SENDIRI" | "WAKIL";
    jurusan?: string;
    wakilNama?: string; wakilNoIc?: string; wakilNoTel?: string;
    wakilAlamat?: string; wakilHubungan?: string;
  }) => {
    try {
      const res = await fetch(`${API_BASE}/public/recipients/${id}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checkedInBy: checkedInBy ?? "Urusetia",
          type: options?.type ?? "SENDIRI",
          jurusan: options?.jurusan,
          ...(options?.type === "WAKIL" && {
            wakilNama: options.wakilNama, wakilNoIc: options.wakilNoIc,
            wakilNoTel: options.wakilNoTel, wakilAlamat: options.wakilAlamat,
            wakilHubungan: options.wakilHubungan,
          }),
        }),
      });
      const data = await parseJson(res);
      if (data.recipient) data.recipient = toCamelCase(data.recipient);
      return data;
    } catch (err: any) {
      return { success: false, message: err.message ?? "Ralat berlaku", recipient: null };
    }
  },

  undoCheckIn: async (id: number, checkedOutBy?: string) => {
    try {
      const res = await authFetch(`${API_BASE}/recipients/${id}/undo-checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkedOutBy: checkedOutBy ?? "Urusetia" }),
      });
      const data = await parseJson(res);
      if (data.recipient) data.recipient = toCamelCase(data.recipient);
      return data;
    } catch (err: any) {
      return { success: false, message: err.message ?? "Ralat berlaku", recipient: null };
    }
  },

  uploadRecipients: async (recipients: CSVRow[], dun: string, fileName: string): Promise<UploadResponse> => {
    const rows = recipients.map(r => ({
      rowNum: r.rowNum, bil: r.bil, nama: r.nama, no_ic: r.no_ic,
      no_telefon: r.no_telefon, alamat: r.alamat,
      institution: r.institution, age: r.age, slot_masa: r.slot_masa,
    }));
    const res = await authFetch(`${API_BASE}/recipients/upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recipients: rows, dun, fileName }),
    });
    return parseJson(res);
  },

  uploadMappedRecipients: async (payload: CsvImportPayload): Promise<UploadResponse> => {
    const res = await authFetch(`${API_BASE}/recipients/upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return parseJson(res);
  },

  getRecipientFields: async (): Promise<{ coreFields: RecipientField[]; customFields: RecipientField[] }> => {
    const res = await authFetch(`${API_BASE}/recipient-fields`);
    const data = await parseJson(res);
    return {
      coreFields: (data.coreFields || []).map((field: any) => ({
        key: field.key,
        name: field.label || field.name,
        required: Boolean(field.required),
        type: "core",
      })),
      customFields: (data.customFields || []).map((field: any) => ({
        id: field.id,
        key: field.key,
        name: field.name,
        type: "custom",
      })),
    };
  },

  createRecipientField: async (name: string): Promise<{ field: RecipientField }> => {
    const res = await authFetch(`${API_BASE}/recipient-fields`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    const data = await parseJson(res);
    return {
      field: {
        id: data.field?.id,
        key: data.field?.key,
        name: data.field?.name,
        type: "custom",
      },
    };
  },

  undoUploadImport: async (uploadLogId: number): Promise<UndoUploadResponse> => {
    const res = await authFetch(`${API_BASE}/upload-logs/${uploadLogId}/undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    return parseJson(res);
  },

  deleteRecipient: async (id: number) => {
    const res = await authFetch(`${API_BASE}/recipients/${id}`, { method: "DELETE" });
    return parseJson(res);
  },

  getUploadLogs: async (): Promise<{ logs: UploadLog[] }> => {
    const res = await authFetch(`${API_BASE}/upload-logs`);
    return toCamelCase(await parseJson(res));
  },

  getAttendanceLogs: async (params?: { page?: number; limit?: number }): Promise<AttendanceLogsResponse> => {
    const q = new URLSearchParams();
    if (params?.page)  q.set("page",  String(params.page));
    if (params?.limit) q.set("limit", String(params.limit));
    const res = await authFetch(`${API_BASE}/attendance-logs?${q}`);
    return toCamelCase(await parseJson(res));
  },
};

// Backwards-compat default export (used in App.tsx / AdminLogin.tsx)
export const client = { auth };
